import type {DeviceSummary} from '@/entities/device/types';
import type {StreamCamera} from './types';

/**
 * Cameras behind a video recorder (NVR), gathered under the recorder.
 *
 * The catalogue (`GET /stream/cameras`) is flat: an NVR with four cameras arrives as
 * four rows sharing one `peer`, and nothing in a row says "recorder". The recorder
 * itself is a device row (`device_type: 'nvr'`) whose `device_id` IS that peer — the
 * box's `hardware_id`, which sv_control keys the channels by (`{peer}:{cameraId}`).
 * Joining the two is the only way to show "NVR, its name, its number, its cameras".
 *
 * A recorder with no channels yet (claimed, but it has not sent its camera list) is
 * still returned: it is the row that says the box exists and is waiting.
 */
export type RecorderGroup = {
  /** The recorder's peer = its device_id = the `sourceId` its settings are keyed by. */
  peer: string;
  name: string;
  machineNumber: string | null;
  channels: StreamCamera[];
  /** The recorder holds its signalling socket now (every channel shares it). */
  online: boolean;
};

export function groupByRecorder(
  cameras: StreamCamera[],
  devices: DeviceSummary[],
): {recorders: RecorderGroup[]; loose: StreamCamera[]} {
  const recorders: RecorderGroup[] = devices
    .filter(d => d.device_type === 'nvr')
    .map(d => {
      const channels = cameras
        .filter(c => c.peer === d.device_id)
        .sort((a, b) => channelOrder(a.deviceId) - channelOrder(b.deviceId));
      return {
        peer: d.device_id,
        name: d.device_name || d.machine_number || d.device_id,
        machineNumber: d.machine_number ?? null,
        channels,
        online: channels.some(c => c.online === true),
      };
    });
  const grouped = new Set(recorders.map(r => r.peer));
  return {recorders, loose: cameras.filter(c => !grouped.has(c.peer))};
}

/** `nvr-x:ch10` sorts after `nvr-x:ch9`: channel numbers, not strings. */
function channelOrder(deviceId: string): number {
  const m = /(\d+)$/.exec(deviceId);
  return m ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

/** Device rows that belong to a recorder — the box and its channels — by device_id. */
export function recorderDeviceIds(devices: DeviceSummary[]): Set<string> {
  const peers = devices.filter(d => d.device_type === 'nvr').map(d => d.device_id);
  return new Set(
    devices
      .filter(d => peers.some(p => d.device_id === p || d.device_id.startsWith(`${p}:`)))
      .map(d => d.device_id),
  );
}
